/**
 * SubCard Component
 *
 * Small information card for nested data within main KPI cards
 * Used in 2x2 grid layouts for sub-categories
 */

import React from 'react'
import { EmptyState } from '../ui/atoms'

interface SubCardProps {
  /** Lucide icon component */
  icon: React.ElementType
  /** Card title */
  title: string
  /** Description text */
  description: string
  /** Main value */
  value: string | number
  /** Unit text */
  unit: string
  /** Meta information (e.g., "0.78% • ต้องดูแล 5,240 รายเร่งด่วน") */
  meta?: string
  /** Color variant */
  variant?: 'blue' | 'amber' | 'emerald' | 'purple' | 'rose' | 'cyan' | 'slate' | 'orange'
  /** Loading state */
  isLoading?: boolean
  /** Custom className */
  className?: string
}

export function SubCard({
  icon: Icon,
  title,
  description,
  value,
  unit,
  meta,
  variant = 'blue',
  isLoading = false,
  className = '',
}: SubCardProps) {
  const variantColors = {
    blue: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
      value: 'text-blue-700',
    },
    amber: {
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-700',
      value: 'text-amber-700',
    },
    emerald: {
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-700',
      value: 'text-emerald-700',
    },
    purple: {
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-700',
      value: 'text-purple-700',
    },
    rose: {
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-700',
      value: 'text-rose-700',
    },
    cyan: {
      iconBg: 'bg-cyan-100',
      iconColor: 'text-cyan-700',
      value: 'text-cyan-700',
    },
    slate: {
      iconBg: 'bg-slate-200',
      iconColor: 'text-slate-700',
      value: 'text-slate-700',
    },
    orange: {
      iconBg: 'bg-korat-orange-100',
      iconColor: 'text-korat-orange-700',
      value: 'text-korat-orange-700',
    },
  }

  const colors = variantColors[variant]

  const formatNumber = (val: string | number): string => {
    if (typeof val === 'string') return val
    return new Intl.NumberFormat('th-TH').format(val)
  }

  return (
    <div
      className={`
        bg-slate-50 rounded-lg border border-slate-100 p-4
        transition-all duration-200 hover:bg-slate-100 hover:border-slate-200
        ${className}
      `}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {/* Icon */}
        <div
          className={`
            w-8 h-8 rounded-md ${colors.iconBg}
            flex items-center justify-center shrink-0
          `}
        >
          <Icon className={`w-4 h-4 ${colors.iconColor}`} aria-hidden />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-slate-900 leading-tight">
            {title}
          </h4>
          <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      {/* Value */}
      {isLoading ? (
        <div className="h-6 bg-slate-200 rounded animate-pulse" />
      ) : value === 0 || value === '0' ? (
        <EmptyState size="sm" message="ไม่มีข้อมูล" />
      ) : (
        <div className="flex items-baseline gap-1.5">
          <span className={`text-lg font-bold ${colors.value} tabular-nums`}>
            {formatNumber(value)}
          </span>
          <span className="text-xs text-slate-600">{unit}</span>
        </div>
      )}

      {/* Meta Information */}
      {meta && (
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          {meta}
        </p>
      )}
    </div>
  )
}
